import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/icon/icon.js";

import "../components/app-shell";
import { api, ApiError } from "../lib/api";
import { sharedStyles } from "../styles/shared";
import { inputValue, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { AuthController } from "../lib/auth-controller";

// ── Wire shapes (internal/modules/inspection: domain.go, service.go) ──

/** One item's tap, exactly as the chip mirrored it (InspectItem in Go). The
 * JSON names deliberately match the tap-URL query parameters (nfc/preview.go):
 * uid, ctr, c (SDMMAC), e (encrypted PICCData) and tt (TagTamper status). */
interface InspectItem {
  ref?: string;
  uid?: string;
  ctr?: string;
  c?: string;
  e?: string;
  tt?: string;
}

interface BatchSummary {
  total: number;
  genuine: number;
  failed: number;
  stolen: number;
  replayed: number;
}

interface BatchResult {
  ref?: string;
  uid?: string;
  result: string;
  product?: string;
  producer?: string;
  stolen?: boolean;
  /** Not sent by the server today — the Go BatchResult carries no custody
   * field (only the recorded Check does). Kept optional so the column lights
   * up if the server starts including it. */
  custody?: string;
  checkId?: string;
  error?: string;
}

interface InspectResponse {
  summary: BatchSummary;
  results: BatchResult[];
}

/** A recorded verification (Check in Go). Immutable evidence. */
interface InspectionCheck {
  id: string;
  uid: string;
  result: string;
  product?: string;
  producer?: string;
  custody?: string;
  stolen: boolean;
  bodyId: string;
  officer?: string;
  at: string;
  location?: string;
  note?: string;
  attestation?: string;
}

interface InspectionBody {
  id: string;
  name: string;
  kind: string;
  jurisdiction?: string;
  createdAt?: string;
}

interface SignedAttestation {
  attestation: {
    v: string;
    uid: string;
    result: string;
    product?: string;
    producer?: string;
    stolen?: boolean;
    body: string;
    officer?: string;
    at: string;
    location?: string;
    nonce: string;
  };
  signature: string;
  keyId: string;
  verifyWith: string;
}

const BODY_KIND_LABELS: Record<string, string> = {
  customs: "Customs authority",
  appraiser: "Appraiser",
  brand_protection: "Brand protection",
};

// Server-side bound (inspection/service.go maxBatch). Checked here so a
// too-large paste fails with a sentence instead of a 400.
const MAX_BATCH = 500;

const HISTORY_LIMITS = [25, 50, 100, 200, 500];

/** Parses one pasted line into an InspectItem.
 *
 * Accepted forms:
 *   https://host/nfc/v1/t?uid=04AB…&ctr=000001&c=1F2E…       (plaintext mirror)
 *   https://host/nfc/v1/t?e=9A0B…&c=1F2E…&tt=OC              (encrypted mirror)
 *   CARTON-17 https://host/nfc/v1/t?uid=…                    (own ref first)
 *   uid=04AB…&ctr=000001&c=1F2E…                             (bare query string)
 *
 * The query parameter names come from nfc/preview.go and are what the chip's
 * SDM mirror writes: uid, ctr, c, e, tt. They map 1:1 onto InspectItem's JSON
 * fields. `ref` defaults to the 1-based line number so a 400-row response can
 * be reconciled against the paste; a leading token before the URL overrides it
 * (a carton number, a case reference).
 */
function parseTapLine(line: string, lineNo: number): InspectItem {
  let ref = String(lineNo);
  let rest = line.trim();

  const m = /^(\S+)\s+(\S+)$/.exec(rest);
  if (m && (m[2].includes("?") || m[2].includes("="))) {
    ref = m[1];
    rest = m[2];
  }

  const qIdx = rest.indexOf("?");
  let qs = qIdx >= 0 ? rest.slice(qIdx + 1) : rest.includes("=") ? rest : "";
  const hIdx = qs.indexOf("#");
  if (hIdx >= 0) qs = qs.slice(0, hIdx);

  const params = new URLSearchParams(qs);
  const item: InspectItem = { ref };
  const uid = params.get("uid");
  const ctr = params.get("ctr");
  const c = params.get("c");
  const e = params.get("e");
  const tt = params.get("tt");
  if (uid) item.uid = uid;
  if (ctr) item.ctr = ctr;
  if (c) item.c = c;
  if (e) item.e = e;
  if (tt) item.tt = tt;
  // A line with no recognizable parameters is still sent: the server records a
  // per-item error and never aborts the batch, which keeps the response's rows
  // aligned with the officer's manifest.
  return item;
}

/** Does this parsed item carry anything a verifier could check? Used only to
 * warn before submitting — unparseable lines are still sent (see above). */
function looksVerifiable(item: InspectItem): boolean {
  return !!(item.e || (item.uid && item.c));
}

// Professional verification for the cross-tenant `inspector` role: customs
// officers, appraisers, brand-protection contractors. Bulk verify a
// consignment, keep the recorded checks as evidence, and mint portable signed
// attestations anyone can verify offline against the published Ed25519 keys —
// no Authentrace account required. The server enforces every gate shown here;
// this page only decides what is worth rendering.
@customElement("inspection-page")
export class InspectionPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .verify-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 0 20px 20px;
      }
      .verify-form md-outlined-text-field {
        width: 100%;
      }
      .paste-hint {
        margin: 0;
        font-size: 0.75rem;
        color: var(--md-sys-color-on-surface-variant);
        line-height: 1.5;
      }
      .register-card {
        display: flex;
        flex-direction: column;
        gap: 16px;
        max-width: 480px;
      }
      .register-card p {
        margin: 0;
        font-size: 0.8125rem;
        color: var(--md-sys-color-on-surface-variant);
        line-height: 1.5;
      }
      .gate {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        text-align: center;
        padding: 56px 24px;
      }
      .gate md-icon {
        --md-icon-size: 40px;
        color: var(--md-sys-color-on-surface-variant);
      }
      .gate h2 {
        margin: 0;
        font: 400 20px/1.3 var(--font-brand, sans-serif);
      }
      .gate p {
        margin: 0;
        max-width: 46ch;
        font-size: 0.875rem;
        color: var(--md-sys-color-on-surface-variant);
        line-height: 1.5;
      }
      .stat .n--bad {
        color: var(--md-sys-color-error);
      }
      .attest-note {
        margin: 0;
        padding: 0 20px 16px;
        font-size: 0.75rem;
        color: var(--md-sys-color-on-surface-variant);
        line-height: 1.5;
        max-width: 82ch;
      }
      .row-actions {
        display: flex;
        gap: 4px;
        justify-content: flex-end;
        align-items: center;
      }
      .cardhead md-outlined-select {
        --md-outlined-select-text-field-container-shape: 999px;
        min-width: 130px;
      }
    `,
  ];

  private auth = new AuthController(this);

  @state() private loading = true;
  @state() private error = "";
  // The API said 409 / ErrNoBody: the account holds the inspector role but is
  // not attached to a registered inspection body yet.
  @state() private needsBody = false;

  // First-run registration.
  @state() private bName = "";
  @state() private bKind = "customs";
  @state() private bJurisdiction = "";
  @state() private registering = false;
  @state() private registerError = "";

  // Bulk verification.
  @state() private rawInput = "";
  @state() private location = "";
  @state() private note = "";
  @state() private verifying = false;
  @state() private verifyError = "";
  @state() private batch?: InspectResponse;

  // Attestations issued this session, by check id (label flips to re-attest).
  @state() private attested: Record<string, boolean> = {};
  @state() private attesting = "";

  // History.
  @state() private checks: InspectionCheck[] = [];
  @state() private historyLimit = 50;
  @state() private historyLoading = false;

  connectedCallback(): void {
    super.connectedCallback();
    if (this.auth.hasRole("inspector")) void this.probe();
    else this.loading = false;
  }

  /** First load doubles as the no-body probe: the history endpoint returns
   * 409 (ErrNoBody) until the caller's organization registered a body. */
  private async probe(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      await this.loadHistory();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        this.needsBody = true;
      } else {
        this.error = errorMessage(err, "Failed to load inspection history.");
      }
    } finally {
      this.loading = false;
    }
  }

  private async loadHistory(): Promise<void> {
    this.historyLoading = true;
    try {
      const resp = await api.get<{ checks: InspectionCheck[] | null }>(
        `/inspection/checks?limit=${this.historyLimit}`,
      );
      // Newest first, whatever order the store returned.
      this.checks = (resp.checks ?? [])
        .slice()
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    } finally {
      this.historyLoading = false;
    }
  }

  private async handleRegister(e: Event): Promise<void> {
    e.preventDefault();
    this.registerError = "";
    if (!this.bName.trim()) {
      this.registerError = "The body's name is required.";
      return;
    }
    this.registering = true;
    try {
      const body = await api.post<InspectionBody>("/inspection/body", {
        name: this.bName.trim(),
        kind: this.bKind,
        jurisdiction: this.bJurisdiction.trim(),
      });
      this.needsBody = false;
      toast(`Registered ${body.name}`);
      await this.probe();
    } catch (err) {
      this.registerError = errorMessage(
        err,
        "Failed to register the inspection body.",
      );
    } finally {
      this.registering = false;
    }
  }

  private get parsedItems(): InspectItem[] {
    return this.rawInput
      .split("\n")
      .map((line, i) => ({ line: line.trim(), no: i + 1 }))
      .filter(({ line }) => line.length > 0)
      .map(({ line, no }) => parseTapLine(line, no));
  }

  private async handleVerify(e: Event): Promise<void> {
    e.preventDefault();
    this.verifyError = "";
    const items = this.parsedItems;
    if (items.length === 0) {
      this.verifyError = "Paste at least one tap URL.";
      return;
    }
    if (items.length > MAX_BATCH) {
      this.verifyError = `At most ${MAX_BATCH} items per consignment — split the paste.`;
      return;
    }
    this.verifying = true;
    try {
      const res = await api.post<InspectResponse>("/inspection/verify", {
        items,
        location: this.location.trim() || undefined,
        note: this.note.trim() || undefined,
      });
      this.batch = res;
      const s = res.summary;
      toast(
        `Verified ${s.total} ${s.total === 1 ? "item" : "items"}: ` +
          `${s.genuine} genuine, ${s.failed} failed` +
          (s.stolen > 0 ? `, ${s.stolen} flagged stolen` : ""),
        { isError: s.failed > 0 || s.stolen > 0 },
      );
      // Every verified item became a recorded check; refresh the evidence log.
      void this.loadHistory().catch(() => undefined);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) this.needsBody = true;
      else this.verifyError = errorMessage(err, "Verification failed.");
    } finally {
      this.verifying = false;
    }
  }

  /** Issues a signed attestation for a recorded check and offers it as a
   * download — a small JSON document anyone can verify offline against the
   * published keys, with no access to us at all. */
  private async attest(checkId: string): Promise<void> {
    this.attesting = checkId;
    try {
      const sa = await api.post<SignedAttestation>(
        `/inspection/checks/${checkId}/attest`,
      );
      const blob = new Blob([JSON.stringify(sa, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attestation-${checkId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      this.attested = { ...this.attested, [checkId]: true };
      toast(
        `Attestation downloaded — verifiable offline against ${sa.verifyWith}`,
        { durationMs: 6000 },
      );
      void this.loadHistory().catch(() => undefined);
    } catch (err) {
      toast(errorMessage(err, "Could not issue the attestation."), true);
    } finally {
      this.attesting = "";
    }
  }

  private formatDateTime(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() < 1970) return "—";
    return d.toLocaleString();
  }

  /** The verdict vocabulary, in the portal's status idiom: color always paired
   * with an icon and a word. "tampered" means the chip is genuine but the
   * TagTamper seal was opened — genuine goods, opened packaging — so it is not
   * painted as a failure. */
  private verdictBadge(result: string, error?: string) {
    switch (result) {
      case "genuine":
        return html`<span class="badge badge--ok"
          >${icon("verified")} Genuine</span
        >`;
      case "tampered":
        return html`<span class="badge badge--neutral"
          >${icon("gpp_maybe")} Genuine · seal opened</span
        >`;
      case "replay":
        return html`<span class="badge badge--bad"
          >${icon("history")} Replayed</span
        >`;
      case "error":
        return html`<span class="badge badge--bad" title=${error ?? ""}
          >${icon("gpp_bad")} Error</span
        >`;
      default:
        return html`<span class="badge badge--bad"
          >${icon("gpp_bad")} ${result || "Failed"}</span
        >`;
    }
  }

  private stolenBadge(stolen: boolean | undefined) {
    return stolen
      ? html`<span class="badge badge--bad">${icon("flag")} Stolen</span>`
      : html`<span class="muted">—</span>`;
  }

  private attestButton(checkId: string | undefined) {
    if (!checkId) return html`<span class="muted">—</span>`;
    const busy = this.attesting === checkId;
    return html`
      <md-text-button
        ?disabled=${busy}
        @click=${() => void this.attest(checkId)}
      >
        <span slot="icon">${icon("description")}</span>
        ${busy ? "Signing…" : this.attested[checkId] ? "Attest again" : "Attest"}
      </md-text-button>
    `;
  }

  // ── Sections ──

  private renderGate() {
    return html`
      <div class="card gate">
        ${icon("shield")}
        <h2>This area is for registered inspection bodies</h2>
        <p>
          Bulk verification and signed attestations are available to customs
          authorities, appraisers and brand-protection contractors holding the
          inspector role. If that is your work, ask your platform operator to
          enable it for your account.
        </p>
      </div>
    `;
  }

  private renderRegister() {
    return html`
      <div class="card register-card">
        <h2>Register your inspection body</h2>
        <p>
          Every check is recorded as evidence against the body that made it —
          evidence with no attributable author is not evidence. Register once;
          verification and attestations unlock immediately. Registration
          requires an organization admin.
        </p>
        <form
          class="form-grid"
          @submit=${(e: Event) => void this.handleRegister(e)}
        >
          <md-outlined-text-field
            label="Body name"
            required
            placeholder="Rotterdam Customs — Container Inspection"
            .value=${this.bName}
            @input=${(e: Event) => (this.bName = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-select
            label="Kind"
            .value=${this.bKind}
            @change=${(e: Event) => (this.bKind = inputValue(e))}
          >
            ${Object.entries(BODY_KIND_LABELS).map(
              ([value, label]) => html`
                <md-select-option value=${value}>
                  <div slot="headline">${label}</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>
          <md-outlined-text-field
            label="Jurisdiction"
            placeholder="NL, or a bloc such as EU"
            supporting-text="For the record, not for enforcement (ISO 3166-1 alpha-2)."
            .value=${this.bJurisdiction}
            @input=${(e: Event) => (this.bJurisdiction = inputValue(e))}
          ></md-outlined-text-field>
          ${this.registerError
            ? html`<p class="error-text">${this.registerError}</p>`
            : null}
          <div class="actions-row">
            <md-filled-button type="submit" ?disabled=${this.registering}>
              <span slot="icon">${icon("verified_user")}</span>
              ${this.registering ? "Registering…" : "Register"}
            </md-filled-button>
          </div>
        </form>
      </div>
    `;
  }

  private renderVerifyForm() {
    const items = this.parsedItems;
    const unverifiable = items.filter((i) => !looksVerifiable(i)).length;
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Verify a consignment</h2>
        </div>
        <form
          class="verify-form"
          @submit=${(e: Event) => void this.handleVerify(e)}
        >
          <md-outlined-text-field
            type="textarea"
            rows="8"
            label="Tap URLs, one per line"
            placeholder="https://…/nfc/v1/t?uid=04AB…&ctr=000001&c=1F2E…"
            .value=${this.rawInput}
            @input=${(e: Event) => (this.rawInput = inputValue(e))}
          ></md-outlined-text-field>
          <p class="paste-hint">
            Paste each item's tap URL as read from the chip. Rows are numbered
            in order; prefix a line with your own reference to override it
            (e.g. <code>CARTON-17 https://…</code>). Up to ${MAX_BATCH} items —
            one bad item never aborts the batch.
          </p>
          <div class="form-row">
            <md-outlined-text-field
              label="Location (optional)"
              placeholder="Terminal 3, bay 12 — case 2026/0417"
              supporting-text="Free text for the record; never inferred from the network."
              .value=${this.location}
              @input=${(e: Event) => (this.location = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Note (optional)"
              .value=${this.note}
              @input=${(e: Event) => (this.note = inputValue(e))}
            ></md-outlined-text-field>
          </div>
          ${this.verifyError
            ? html`<p class="error-text">${this.verifyError}</p>`
            : null}
          <div class="actions-row">
            <md-filled-button
              type="submit"
              ?disabled=${this.verifying || items.length === 0}
            >
              <span slot="icon">${icon("contactless")}</span>
              ${this.verifying
                ? "Verifying…"
                : items.length > 0
                  ? `Verify ${items.length} ${items.length === 1 ? "item" : "items"}`
                  : "Verify"}
            </md-filled-button>
            ${unverifiable > 0
              ? html`<span class="paste-hint">
                  ${unverifiable}
                  ${unverifiable === 1 ? "line carries" : "lines carry"} no
                  recognizable tap parameters and will come back as errors.
                </span>`
              : null}
          </div>
        </form>
      </div>
    `;
  }

  private renderSummary(s: BatchSummary) {
    const tile = (n: number, label: string, bad = false) => html`
      <div class="stat">
        <div class="n ${bad && n > 0 ? "n--bad" : ""}">${n}</div>
        <div class="l">${label}</div>
      </div>
    `;
    return html`
      <div class="stats">
        ${tile(s.total, "Checked")} ${tile(s.genuine, "Genuine")}
        ${tile(s.failed, "Failed", true)} ${tile(s.replayed, "Replayed", true)}
        ${tile(s.stolen, "Flagged stolen", true)}
      </div>
    `;
  }

  private renderResults() {
    const batch = this.batch;
    if (!batch) return null;
    return html`
      ${this.renderSummary(batch.summary)}
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Results <small>· ${batch.results.length}</small></h2>
          <md-text-button @click=${() => (this.batch = undefined)}>
            Clear
          </md-text-button>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Ref</th>
              <th>Verdict</th>
              <th>Product</th>
              <th>Producer</th>
              <th>Stolen</th>
              <th>Custody</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${batch.results.map(
              (r) => html`
                <tr>
                  <td>
                    <b>${r.ref || "—"}</b>
                    ${r.uid ? html`<small class="mono">${r.uid}</small>` : null}
                  </td>
                  <td>
                    ${this.verdictBadge(r.result, r.error)}
                    ${r.error ? html`<small>${r.error}</small>` : null}
                  </td>
                  <td>${r.product || html`<span class="muted">—</span>`}</td>
                  <td>${r.producer || html`<span class="muted">—</span>`}</td>
                  <td>${this.stolenBadge(r.stolen)}</td>
                  <td>${r.custody || html`<span class="muted">—</span>`}</td>
                  <td>
                    <div class="row-actions">${this.attestButton(r.checkId)}</div>
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
        <p class="attest-note">
          An attestation is a small signed JSON document — portable proof that
          this verification happened. Anyone can check its Ed25519 signature
          offline against the published keys at
          <code>/.well-known/authentrace-attestation-keys.json</code>, with no
          Authentrace account and no access to us at all.
        </p>
      </div>
    `;
  }

  private renderHistory() {
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>
            Recent checks
            ${this.checks.length > 0
              ? html`<small>· ${this.checks.length}</small>`
              : null}
          </h2>
          <md-outlined-select
            label="Show"
            .value=${String(this.historyLimit)}
            @change=${(e: Event) => {
              const next = Number.parseInt(inputValue(e), 10);
              if (Number.isFinite(next) && next !== this.historyLimit) {
                this.historyLimit = next;
                void this.loadHistory().catch(() => undefined);
              }
            }}
          >
            ${HISTORY_LIMITS.map(
              (n) => html`
                <md-select-option value=${String(n)}>
                  <div slot="headline">Last ${n}</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>
        </div>
        ${this.historyLoading
          ? html`<div class="center-loading">
              <md-circular-progress indeterminate></md-circular-progress>
            </div>`
          : this.checks.length === 0
            ? html`<div class="empty">
                No checks recorded yet — verify a consignment above.
              </div>`
            : html`<div class="table-scroll"><table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Item</th>
                    <th>Verdict</th>
                    <th>Producer</th>
                    <th>Stolen</th>
                    <th>Location</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${this.checks.map(
                    (ch) => html`
                      <tr>
                        <td>${this.formatDateTime(ch.at)}</td>
                        <td>
                          ${ch.product || html`<span class="muted">—</span>`}
                          <small class="mono">${ch.uid}</small>
                        </td>
                        <td>${this.verdictBadge(ch.result)}</td>
                        <td>
                          ${ch.producer || html`<span class="muted">—</span>`}
                          ${ch.custody ? html`<small>${ch.custody}</small>` : null}
                        </td>
                        <td>${this.stolenBadge(ch.stolen)}</td>
                        <td>${ch.location || html`<span class="muted">—</span>`}</td>
                        <td>
                          <div class="row-actions">
                            ${ch.attestation
                              ? html`<span class="badge badge--ok"
                                  >${icon("verified_user")} Attested</span
                                >`
                              : null}
                            ${this.attestButton(ch.id)}
                          </div>
                        </td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table></div>`}
      </div>
    `;
  }

  render() {
    // Cross-tenant professional area: server-enforced; we render a calm
    // explanation rather than a broken console for everybody else.
    if (!this.auth.hasRole("inspector")) {
      return html`
        <app-shell active="/inspection">
          <div class="page">${this.renderGate()}</div>
        </app-shell>
      `;
    }
    return html`
      <app-shell active="/inspection">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Inspection</h1>
              <p>
                Bulk verification for professionals — every check is recorded as
                evidence, and any finding can be issued as a signed attestation
                verifiable offline.
              </p>
            </div>
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : this.needsBody
                ? this.renderRegister()
                : html`
                    ${this.renderVerifyForm()} ${this.renderResults()}
                    ${this.renderHistory()}
                  `}
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "inspection-page": InspectionPage;
  }
}
